import { memo } from 'react';

// Chain logos
import ethereumLogo from '@/assets/chains/ethereum.svg';
import polygonLogo from '@/assets/chains/polygon.svg';
import baseLogo from '@/assets/chains/base.svg';
import arbitrumLogo from '@/assets/chains/arbitrum.svg';
import optimismLogo from '@/assets/chains/optimism.svg';
import avalancheLogo from '@/assets/chains/avalanche.svg';
import bnbLogo from '@/assets/chains/bnb.svg';
import solanaLogo from '@/assets/chains/solana.svg';

// Token logos
import btcLogo from '@/assets/crypto/btc.svg';
import ethLogo from '@/assets/crypto/eth.svg';
import solLogo from '@/assets/crypto/sol.svg';
import usdtLogo from '@/assets/crypto/usdt.svg';
import usdcLogo from '@/assets/crypto/usdc.svg';
import maticLogo from '@/assets/crypto/matic.svg';
import avaxLogo from '@/assets/crypto/avax.svg';
import arbLogo from '@/assets/crypto/arb.svg';
import opLogo from '@/assets/crypto/op.svg';
import trxLogo from '@/assets/crypto/trx.svg';

const topRowLogos = [
  { src: ethereumLogo, alt: 'Ethereum', name: 'Ethereum', color: '#627EEA' },
  { src: btcLogo, alt: 'Bitcoin', name: 'Bitcoin', color: '#F7931A' },
  { src: solanaLogo, alt: 'Solana', name: 'Solana', color: '#9945FF' },
  { src: usdtLogo, alt: 'USDT', name: 'USDT', color: '#26A17B' },
  { src: polygonLogo, alt: 'Polygon', name: 'Polygon', color: '#8247E5' },
  { src: usdcLogo, alt: 'USDC', name: 'USDC', color: '#2775CA' },
  { src: baseLogo, alt: 'Base', name: 'Base', color: '#0052FF' },
  { src: avaxLogo, alt: 'Avalanche', name: 'AVAX', color: '#E84142' },
  { src: arbitrumLogo, alt: 'Arbitrum', name: 'Arbitrum', color: '#28A0F0' },
  { src: trxLogo, alt: 'Tron', name: 'TRX', color: '#FF0013' },
];

const bottomRowLogos = [
  { src: ethLogo, alt: 'ETH', name: 'ETH', color: '#627EEA' },
  { src: solLogo, alt: 'SOL', name: 'SOL', color: '#9945FF' },
  { src: bnbLogo, alt: 'BNB', name: 'BNB', color: '#F3BA2F' },
  { src: maticLogo, alt: 'MATIC', name: 'MATIC', color: '#8247E5' },
  { src: optimismLogo, alt: 'Optimism', name: 'Optimism', color: '#FF0420' },
  { src: avalancheLogo, alt: 'Avalanche Chain', name: 'Avalanche', color: '#E84142' },
  { src: arbLogo, alt: 'ARB', name: 'ARB', color: '#28A0F0' },
  { src: opLogo, alt: 'OP', name: 'OP', color: '#FF0420' },
  { src: usdtLogo, alt: 'Tether', name: 'Tether', color: '#26A17B' },
  { src: btcLogo, alt: 'BTC', name: 'BTC', color: '#F7931A' },
];

interface MarqueeRowProps {
  logos: { src: string; alt: string; name: string; color: string }[];
  direction: 'left' | 'right';
}

const MarqueeRow = memo(({ logos, direction }: MarqueeRowProps) => {
  // Duplicate logos for seamless loop
  const duplicatedLogos = [...logos, ...logos, ...logos];
  
  return (
    <div className="relative overflow-hidden py-3 md:py-4 group/marquee">
      {/* Gradient masks */}
      <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />
      
      <div 
        className={`flex gap-4 md:gap-6 group-hover/marquee:[animation-play-state:paused] ${
          direction === 'left' ? 'animate-marquee-left' : 'animate-marquee-right'
        }`}
        style={{ width: 'max-content' }}
      >
        {duplicatedLogos.map((logo, index) => (
          <div
            key={`${logo.alt}-${index}`}
            className="flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-2.5 rounded-full bg-white/[0.03] backdrop-blur-sm border border-white/[0.08] flex-shrink-0 hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300 group/item"
            style={{
              boxShadow: `0 0 20px ${logo.color}15, 0 0 40px ${logo.color}08`,
            }}
          >
            <div 
              className="relative w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center"
              style={{
                background: `radial-gradient(circle, ${logo.color}20 0%, transparent 70%)`,
              }}
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="w-5 h-5 md:w-6 md:h-6 object-contain relative z-10"
                loading="lazy"
              />
              {/* Glow effect */}
              <div 
                className="absolute inset-0 rounded-full blur-md opacity-40 group-hover/item:opacity-60 transition-opacity"
                style={{ background: logo.color }}
              />
            </div>
            <span className="text-xs md:text-sm font-medium text-white/70 group-hover/item:text-white/90 transition-colors">
              {logo.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
});

MarqueeRow.displayName = 'MarqueeRow';

export const LogoMarquee = memo(() => {
  return (
    <section className="w-full py-6 md:py-10 bg-black overflow-hidden">
      <div className="space-y-2 md:space-y-3">
        <MarqueeRow logos={topRowLogos} direction="left" />
        <MarqueeRow logos={bottomRowLogos} direction="right" />
      </div>
    </section>
  );
});

LogoMarquee.displayName = 'LogoMarquee';
